// Shared "is this doc ready to publish" checks for the CMS.
//
// Two sets of requirements — one for property listings, one for
// office buildings. Both mirror what the public site actually reads
// when rendering cards + detail pages, so an "incomplete" doc is
// literally one that would render broken on the public site.
//
// The exported helpers are used by:
//   - the Incomplete filter chip on /cms/property-search + /cms/offices
//     (via the pagination hooks' filterCachedDocs)
//   - the IncompleteWarning banner on the PropertyListingDetail +
//     OfficeBuildingDetail pages
//
// Listing requirements mirror ravenv20/src/Pages/InvestmentSales/tabs/
// WebsiteTab.jsx `PUBLISH_REQUIREMENTS` — kept in sync so the CMS
// warning surfaces the same items the Raven publish-gate blocks on.

// -------- Listings (`listings` collection) ---------------------------

function listingRequirements(doc) {
  const isForSale = doc?.listing_type === "For Sale";
  return [
    {
      key: "title",
      label: "Property name / website title",
      where: "Edit in Raven → Property Details",
      check: (d) => Boolean(d.property_name || d.website_title),
    },
    {
      key: "address",
      label: "Address",
      where: "Edit in Raven → Property Details",
      check: (d) => Boolean(d.address),
    },
    {
      key: "category",
      label: "Category",
      where: "Edit in Raven → Property Details",
      check: (d) => Boolean(d.category),
    },
    {
      key: "listing_type",
      label: "Listing type (For Sale / For Lease)",
      where: "Edit in Raven → Property Details",
      check: (d) => Boolean(d.listing_type),
    },
    {
      key: "photo",
      label: "Primary photo",
      where: "Photos tab (this page's sidebar)",
      check: (d) => Boolean(d.primaryPhotoUrl),
    },
    isForSale
      ? {
          key: "price",
          label: "Sale price (websitePrice / totalWebsitePrice / total_price / selling_price)",
          where: "Edit in Raven → Property Details",
          check: (d) =>
            Boolean(
              d.websitePrice ||
                d.totalWebsitePrice ||
                d.total_price ||
                d.selling_price,
            ),
        }
      : {
          key: "price",
          label: "Lease rate (leaseRate — per sqm/month)",
          where: "Edit in Raven → Property Details",
          check: (d) => Boolean(d.leaseRate),
        },
    {
      key: "pipeline",
      label: 'Pipeline stage set to "30%"',
      where: "Edit in Raven → Property Details",
      check: (d) => d.pipeline_status === "30%",
    },
  ];
}

/**
 * Returns an array of missing requirements for a listing doc, in the
 * order they appear in the checklist. Empty array = complete.
 */
export function getListingMissingFields(doc) {
  if (!doc) return [];
  return listingRequirements(doc)
    .filter((req) => !req.check(doc))
    .map(({ key, label, where }) => ({ key, label, where }));
}

/** True when the listing has zero missing requirements. */
export function isListingComplete(doc) {
  return getListingMissingFields(doc).length === 0;
}

// -------- Buildings (`masterlist` collection) ------------------------

// Buildings render on /offices (grid card) and /offices/[slug] (detail).
// Card needs: buildingName, address, city, district, photo.
// Detail also uses: description in the "Property Overview" block.
// Everything else (grade, GLA, floor plate, systems…) is helpful but
// not required — the page renders gracefully without them.
function buildingRequirements() {
  return [
    {
      key: "buildingName",
      label: "Building name",
      where: "Edit in Raven → Commercial Leasing → Masterlist",
      check: (d) => Boolean(d.buildingName),
    },
    {
      key: "address",
      label: "Address",
      where: "Edit in Raven → Commercial Leasing → Masterlist",
      check: (d) => Boolean(d.address),
    },
    {
      key: "city",
      label: "City",
      where: "Edit in Raven → Commercial Leasing → Masterlist",
      check: (d) => Boolean(d.city),
    },
    {
      key: "photo",
      label: "Primary photo",
      where: "Photo section (this page's sidebar)",
      check: (d) => Boolean(d.primaryPhotoUrl),
    },
    {
      key: "description",
      label: "Building description",
      where: "Description section (this page's main column)",
      check: (d) => Boolean((d.description || "").trim()),
    },
  ];
}

export function getBuildingMissingFields(doc) {
  if (!doc) return [];
  return buildingRequirements()
    .filter((req) => !req.check(doc))
    .map(({ key, label, where }) => ({ key, label, where }));
}

export function isBuildingComplete(doc) {
  return getBuildingMissingFields(doc).length === 0;
}
